import { useEffect, useState } from 'react';
import { Box, CircularProgress, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { Cancel, CheckCircle } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DoctorRoles, PatientRoles, Role } from '../../config/DiactiveTypes';
import { getPendingAttentionMails } from '../../services/MailService';
import { getUserByEmail, getUserFullName } from '../../services/UserService';
import { formatDateTime, getPatientGroupName } from '../../utils/DiactiveUtils';
import { EmailTemplateSubjects, getMailDeliveryStatusDisplay, MailDeliveryStatusIconVariant } from '../../utils/MailUtils';
import HeaderFilterSelect from '../common/HeaderFilterSelect';

const PendingMailStatusFilter = {
  All: 'all',
  Pending: 'pending',
  Resolved: 'resolved',
};
const PendingMailGroupFilter = {
  All: 'all',
  Control: 'control',
  Intervention: 'intervention',
  Doctor: 'doctor',
};

const PendingMailTable = ({ role }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(() => {
    const statusFromUrl = searchParams.get('status');
    const isValid = Object.values(PendingMailStatusFilter).includes(statusFromUrl);
    return isValid ? statusFromUrl : PendingMailStatusFilter.Pending;
  });
  const [selectedGroup, setSelectedGroup] = useState(() => {
    const groupFromUrl = searchParams.get('group');
    const isValid = Object.values(PendingMailGroupFilter).includes(groupFromUrl);
    return isValid ? groupFromUrl : PendingMailGroupFilter.All;
  });

  const getMailSubject = (category, subject) => EmailTemplateSubjects[category] ?? subject ?? category ?? '—';

  const renderStatusCell = (row) => {
    const isManuallyResolved = row?.resolutionStatus === "manually_resolved";
    const { label, iconVariant } = getMailDeliveryStatusDisplay(row?.status);
    if (!iconVariant) {
      return label ? <Typography variant="body2">{label}</Typography> : null;
    }

    const Icon = iconVariant === MailDeliveryStatusIconVariant.Error ? Cancel : CheckCircle;
    const color =
      iconVariant === MailDeliveryStatusIconVariant.Error
        ? 'error'
        : iconVariant === MailDeliveryStatusIconVariant.Primary
          ? 'primary'
          : 'success';

    const baseStatus = (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Icon fontSize="small" color={color} />
        <Typography variant="body2">{label}</Typography>
      </Stack>
    );
    const reason = row?.reason;
    const statusWithReasonTooltip = reason ? (
      <Tooltip title={reason} placement="top" enterDelay={300}>
        <Box sx={{ display: 'inline-flex' }}>{baseStatus}</Box>
      </Tooltip>
    ) : baseStatus;

    if (!isManuallyResolved) {
      return statusWithReasonTooltip;
    }

    const resolvedTooltipTitle = (
      <Stack spacing={0.5}>
        <Typography variant="caption">
          Resuelto por: {row?.resolutionResolvedByName ?? row?.resolutionResolvedBy ?? '—'}
        </Typography>
        <Typography variant="caption">{formatDateTime(row?.resolutionUpdatedAt)}</Typography>
      </Stack>
    );

    return (
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        {statusWithReasonTooltip}
        <Tooltip title={resolvedTooltipTitle} arrow>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: 'fit-content' }}>
            <CheckCircle fontSize="small" color="success" />
            <Typography variant="caption" color="success.main">
              Resuelto manualmente
            </Typography>
          </Stack>
        </Tooltip>
      </Stack>
    );
  };

  const refreshItems = async () => {
    setLoading(true);
    try {
      const mails = await getPendingAttentionMails();

      const uniqueEmails = [...new Set(mails.map((m) => m?.patientEmail).filter(Boolean))];
      const usersByEmail = Object.fromEntries(
        await Promise.all(
          uniqueEmails.map(async (email) => {
            try {
              const user = await getUserByEmail(email);
              return [email, user];
            } catch {
              return [email, null];
            }
          })
        )
      );

      const uniqueResolverIds = [...new Set(
        mails
          .map((m) => m?.resolutionResolvedBy)
          .filter((id) => Boolean(id))
      )];
      const resolverNameEntries = await Promise.all(
        uniqueResolverIds.map(async (userId) => {
          try {
            const fullName = await getUserFullName(userId);
            return [userId, fullName];
          } catch {
            return [userId, null];
          }
        })
      );
      const resolverNamesById = Object.fromEntries(resolverNameEntries);

      const enriched = mails.map((m) => {
        const user = m?.patientEmail ? usersByEmail[m.patientEmail] : null;
        return {
          ...m,
          patientId: user?.id ?? null,
          patientNickname: user?.nickname ?? null,
          patientName: user?.fullName ?? null,
          patientEmail: user?.email ?? m?.patientEmail ?? null,
          patientRole: user?.role ?? null,
          resolutionResolvedByName:
            m?.resolutionResolvedBy ? (resolverNamesById[m.resolutionResolvedBy] ?? null) : null
        };
      }).filter((item) => Boolean(item.patientId) && (PatientRoles.includes(item.patientRole) || DoctorRoles.includes(item.patientRole)));

      setItems(enriched);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('status', selectedStatus);
      next.set('group', selectedGroup);
      return next;
    }, { replace: true });
  }, [selectedStatus, selectedGroup, setSearchParams]);

  const goToDetailsMail = (userId, userRole) => {
    if (!userId) return;
    if (PatientRoles.includes(userRole)) {
      navigate(`/patient/view/${userId}/mail`, { state: { fromFilteredTable: true } });
      return;
    }
    if (DoctorRoles.includes(userRole)) {
      navigate(`/doctor/view/${userId}/mail`, { state: { fromFilteredTable: true } });
    }
  };

  const patientDisplay = (row) => {
    if (role === Role.Admin) {
      return row?.patientNickname ?? row?.patientName ?? row?.patientEmail ?? '—';
    }
    return row?.patientName ?? row?.patientEmail ?? '—';
  };
  const groupDisplay = (row) => {
    if (DoctorRoles.includes(row?.patientRole)) return 'Médico';
    return getPatientGroupName(row?.patientRole) || '—';
  };

  const itemsForStatusCounts = items.filter((item) => {
    if (selectedGroup === PendingMailGroupFilter.All) return true;
    if (selectedGroup === PendingMailGroupFilter.Control) return item?.patientRole === Role.Ctrl25;
    if (selectedGroup === PendingMailGroupFilter.Intervention) return item?.patientRole === Role.Intr25;
    if (selectedGroup === PendingMailGroupFilter.Doctor) return DoctorRoles.includes(item?.patientRole);
    return true;
  });
  const itemsForGroupCounts = items.filter((item) => {
    if (selectedStatus === PendingMailStatusFilter.All) return true;
    const isResolved = item?.resolutionStatus === "manually_resolved";
    return selectedStatus === PendingMailStatusFilter.Resolved ? isResolved : !isResolved;
  });

  const statusCounts = {
    all: itemsForStatusCounts.length,
    pending: itemsForStatusCounts.filter((item) => item?.resolutionStatus !== "manually_resolved").length,
    resolved: itemsForStatusCounts.filter((item) => item?.resolutionStatus === "manually_resolved").length
  };
  const groupCounts = {
    all: itemsForGroupCounts.length,
    control: itemsForGroupCounts.filter((item) => item?.patientRole === Role.Ctrl25).length,
    intervention: itemsForGroupCounts.filter((item) => item?.patientRole === Role.Intr25).length,
    doctor: itemsForGroupCounts.filter((item) => DoctorRoles.includes(item?.patientRole)).length
  };

  const statusOptions = [
    { value: PendingMailStatusFilter.Pending, label: 'Pendientes', count: statusCounts.pending },
    { value: PendingMailStatusFilter.Resolved, label: 'Resueltos', count: statusCounts.resolved },
  ];
  const groupOptions = [
    { value: PendingMailGroupFilter.Control, label: 'Control', count: groupCounts.control },
    { value: PendingMailGroupFilter.Intervention, label: 'Intervención', count: groupCounts.intervention },
    { value: PendingMailGroupFilter.Doctor, label: 'Médico', count: groupCounts.doctor },
  ];
  const selectedStatusForSelect = selectedStatus === PendingMailStatusFilter.All ? '' : selectedStatus;
  const selectedGroupForSelect = selectedGroup === PendingMailGroupFilter.All ? '' : selectedGroup;

  const filteredItems = items.filter((item) => {
    let matchesStatus = true;
    if (selectedStatus !== PendingMailStatusFilter.All) {
      const isResolved = item?.resolutionStatus === "manually_resolved";
      matchesStatus = selectedStatus === PendingMailStatusFilter.Resolved ? isResolved : !isResolved;
    }

    let matchesGroup = true;
    if (selectedGroup !== PendingMailGroupFilter.All) {
      if (selectedGroup === PendingMailGroupFilter.Control) matchesGroup = item?.patientRole === Role.Ctrl25;
      if (selectedGroup === PendingMailGroupFilter.Intervention) matchesGroup = item?.patientRole === Role.Intr25;
      if (selectedGroup === PendingMailGroupFilter.Doctor) matchesGroup = DoctorRoles.includes(item?.patientRole);
    }

    return matchesStatus && matchesGroup;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 180, minWidth: 180 }}>Fecha</TableCell>
            <TableCell sx={{ width: 340, minWidth: 340 }}>Nombre</TableCell>
            <TableCell sx={{ width: 180, minWidth: 180 }}>
              <HeaderFilterSelect
                label="Grupo"
                value={selectedGroupForSelect}
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedGroup(value === '' ? PendingMailGroupFilter.All : value);
                }}
                options={groupOptions}
                totalCount={itemsForGroupCounts.length}
                minWidth={0}
              />
            </TableCell>
            <TableCell sx={{ width: 520, minWidth: 520 }}>Asunto</TableCell>
            <TableCell sx={{ width: 220, minWidth: 220 }}>
              <HeaderFilterSelect
                label="Estado"
                value={selectedStatusForSelect}
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedStatus(value === '' ? PendingMailStatusFilter.All : value);
                }}
                options={statusOptions}
                totalCount={itemsForStatusCounts.length}
                minWidth={0}
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredItems.map((row) => (
            <TableRow
              key={row.id}
              hover
              onClick={() => goToDetailsMail(row.patientId, row.patientRole)}
              sx={{
                cursor: (PatientRoles.includes(row.patientRole) || DoctorRoles.includes(row.patientRole)) ? 'pointer' : 'default',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <TableCell sx={{ py: 1.5 }}>{formatDateTime(row.sentAt)}</TableCell>
              <TableCell sx={{ py: 1.5 }}>
                <Typography variant="body2">{patientDisplay(row)}</Typography>
              </TableCell>
              <TableCell sx={{ py: 1.5 }}>
                <Typography variant="body2">{groupDisplay(row)}</Typography>
              </TableCell>
              <TableCell sx={{ py: 1.5, width: 480, minWidth: 480 }}>
                <Tooltip title={getMailSubject(row.category, row.subject)} placement="top" enterDelay={300}>
                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: 460,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {getMailSubject(row.category, row.subject)}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ py: 1.5 }}>{renderStatusCell(row)}</TableCell>
            </TableRow>
          ))}
          {filteredItems.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary" align="center">
                  No hay incidencias de correo.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PendingMailTable;

